import React, { useState } from 'react';
import './ModernNoteCard.css';

const ModernNoteCard = ({ 
  note, 
  onEdit, 
  onDelete, 
  onFavorite, 
  onComplete, 
  onSelect,
  selectionMode = false,
  isSelected = false,
  onToggleSelect,
  isEditing,
  onSave,
  onCancel,
  editedNote,
  setEditedNote,
  editedEmailSubject,
  setEditedEmailSubject,
  editedLink,
  setEditedLink,
  selectedTab,
  onTabClick,
  renderTabContent,
  onAddNewBox,
  newBox,
  setNewBox,
  onSaveNewBox,
  onDeleteBox,
  editingNoteId
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleCardClick = () => {
    if (selectionMode) {
      onToggleSelect && onToggleSelect(note.noteID);
      return;
    }
    onSelect && onSelect(note);
  };

  const handleCheckboxChange = (event) => {
    event.stopPropagation();
    onToggleSelect && onToggleSelect(note.noteID);
  };

  const getStatusColor = (completed, active) => {
    // Handle both boolean and string formats from database
    const isCompleted = completed === true || completed === 'True' || completed === 'true';
    const isActive = active === true || active === 'True' || active === 'true';
    
    if (isCompleted) return 'var(--success-500)';
    if (!isActive) return 'var(--error-500)';
    return 'var(--warning-500)';
  };

  const getStatusText = (completed, active) => {
    // Handle both boolean and string formats from database
    const isCompleted = completed === true || completed === 'True' || completed === 'true';
    const isActive = active === true || active === 'True' || active === 'true';
    
    if (isCompleted) return 'Completed';
    if (!isActive) return 'Inactive';
    return 'Active';
  };

  const getCategoryColor = (category) => {
    if (!category) return '#94a3b8'; // var(--secondary-400)
    const lowerCategory = category.toLowerCase();
    switch (lowerCategory) {
      case 'blue':
        return '#0ea5e9'; // var(--primary-500)
      case 'red':
        return '#ef4444'; // var(--error-500)
      case 'green':
        return '#22c55e'; // var(--success-500)
      case 'purple':
        return '#8b5cf6';
      case 'pink':
        return '#ec4899';
      case 'yellow':
        return '#f59e0b'; // var(--warning-500)
      case 'orange':
        return '#f97316';
      case 'light blue':
      case 'ltblue':
        return '#06b6d4';
      default:
        return '#94a3b8'; // var(--secondary-400)
    }
  };

  // Helper function to get the effective category for a note
  // Priority order:
  // 1. Manual tag with key "color" (case-insensitive) - highest priority
  // 2. AI categorization from aiPromptResults.category[0].color.value - fallback
  const getEffectiveCategory = (note) => {
    // First check for manual color tags (case-insensitive)
    const manualTags = note.manualPromptResults?.manualTags || {};
    const colorTag = Object.entries(manualTags).find(([key, value]) => 
      key.toLowerCase() === 'color' && value
    );
    
    if (colorTag) {
      return colorTag[1]; // Return the manual color value
    }
    
    // Fall back to AI categorization
    return note.aiPromptResults?.category?.[0]?.color?.value;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  return (
    <div 
      className={`modern-note-card ${isHovered ? 'hovered' : ''} ${(note.completed === true || note.completed === 'True' || note.completed === 'true') ? 'completed' : ''} ${selectionMode ? 'selection-mode' : ''} ${selectionMode && isSelected ? 'selected' : ''}`}
      data-note-id={note.noteID}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleCardClick}
    >
      {selectionMode && (
        <div className="selection-checkbox" onClick={(event) => event.stopPropagation()}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={handleCheckboxChange}
            aria-label="Select note for Pack-It conversion"
          />
        </div>
      )}
      {/* Card Header */}
      <div className="card-header">
        <div className="card-title-section">
          <h3 className="card-title">
            {note.manualPromptResults?.emailSubject || 'Untitled Note'}
          </h3>
          <div className="card-meta">
            <span className="card-date">
              {formatDate(note.noteCreationDate)}
            </span>
            <div 
              className="status-indicator"
              style={{ backgroundColor: getStatusColor(note.completed, note.noteActive) }}
            >
              {getStatusText(note.completed, note.noteActive)}
            </div>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="card-actions">
          <button
            className={`action-btn edit-btn ${isEditing ? 'active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              onEdit(note.noteID, note.manualPromptResults?.bodyText || '', note.manualPromptResults?.url || '', note.manualPromptResults?.emailSubject || '');
            }}
            title="Edit Note"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </button>
          
          <button
            className={`action-btn favorite-btn ${note.manualPromptResults?.manualTags?.Favorite ? 'active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              onFavorite(note.noteID);
            }}
            title="Toggle Favorite"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
          </button>
          
          <button
            className={`action-btn complete-btn ${(note.completed === true || note.completed === 'True' || note.completed === 'true') ? 'active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              onComplete(note.noteID);
            }}
            title="Toggle Complete"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20,6 9,17 4,12"/>
            </svg>
          </button>
          
          <button
            className="action-btn delete-btn"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(note.noteID, note.lastEdited);
            }}
            title="Delete Note"
          >
            <i className="fas fa-trash"></i>
          </button>
        </div>
      </div>

      {/* Card Content */}
      <div className="card-content">
        {!isEditing ? (
          <>
            <p className="card-description">
              {truncateText(note.manualPromptResults?.bodyText || 'No description available')}
            </p>
            
            {note.manualPromptResults?.url && note.manualPromptResults.url.length > 0 && (
              <a 
                href={note.manualPromptResults.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="card-link"
                onClick={(e) => e.stopPropagation()}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                </svg>
                View Link
              </a>
            )}
          </>
        ) : (
          <div className="edit-form">
            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                value={editedEmailSubject}
                onChange={(e) => setEditedEmailSubject(e.target.value)}
                className="form-input"
                placeholder="Enter note title"
              />
            </div>
            
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={editedNote}
                onChange={(e) => setEditedNote(e.target.value)}
                className="form-textarea"
                placeholder="Enter note description"
                rows="4"
              />
            </div>
            
            <div className="form-group">
              <label>Link</label>
              <input
                type="url"
                value={editedLink}
                onChange={(e) => setEditedLink(e.target.value)}
                className="form-input"
                placeholder="Enter URL"
              />
            </div>
            
            <div className="form-actions">
              <button 
                className="btn btn-primary"
                onClick={(e) => {
                  e.stopPropagation();
                  onSave(note.noteID);
                }}
              >
                Save Changes
              </button>
              <button 
                className="btn btn-secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  onCancel();
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Card Footer - Tabs */}
      <div className="card-footer">
        <div className="tab-navigation">
          {['Note Details', 'My Tags', 'Recommendations'].map(tabName => (
            <button
              key={tabName}
              className={`tab-btn ${selectedTab === `${note.noteID}-${tabName}` ? 'active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                onTabClick(note.noteID, tabName);
              }}
            >
              {tabName}
                             {tabName === 'Note Details' && (
                 <span
                   className="color-indicator"
                   style={{
                     backgroundColor: getCategoryColor(getEffectiveCategory(note))
                   }}
                   title={`Category: ${getEffectiveCategory(note) || 'None'}`}
                 />
               )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {['Note Details', 'My Tags', 'Recommendations'].map(tabName => (
          selectedTab === `${note.noteID}-${tabName}` && (
            <div key={tabName} className="tab-content">
              <div className="tab-content-header">
                <h4>{tabName}</h4>
                {isEditing && (
                  <button
                    className="add-tag-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddNewBox(note.noteID, tabName);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19"/>
                      <line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                    Add Tag
                  </button>
                )}
              </div>
              <div className="tab-content-body">
                {renderTabContent(note, tabName).map((content, index) => (
                  <div key={index} className="tag-item">
                    {content}
                  </div>
                ))}
              </div>
            </div>
          )
        ))}
      </div>
    </div>
  );
};

export default ModernNoteCard;
